// Imagine — turning a dream entry into an image, from approval to attachment.
//
// The feature's whole claim is the two-stage privacy model, so the interface is built around it
// rather than around the picture. The raw entry goes to Cloudflare's own model, which returns a
// de-identified visual description; that description is shown to the user, in an editable field,
// and only what they approve is sent to the image provider. The approval step is therefore not a
// loading screen with a button on it — it is the point of the feature, and it says plainly what is
// about to leave.
//
// Everything is owned here so the composer can drop the component in with one line: it renders
// nothing when closed, portals its own overlays, and holds every step, error and quota state
// internally. The portal is not decoration — `.oscroll` is `position: absolute; z-index: 1` and so a
// stacking context, and anything rendered inside it paints underneath the nav pill no matter what
// z-index it claims. See SettingsKit's confirmation dialog for the same fix.
//
// One asymmetry worth remembering while reading this: the generated image is NOT encrypted (it is a
// plain PNG behind a 15-minute signed URL), but an entry photo IS. So "Use this vision" cannot just
// hand the URL to the entry — it downloads the bytes and puts them through the same seal-then-upload
// path as a photo from the camera roll.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  DREAM_VISION_PROMPT_MAX_WORDS,
  buildRefinedPrompt,
  canGenerate,
  countWords,
  defaultVisionStyle,
  dreamVisionErrorMessage,
  fetchVisionImage,
  fetchVisionUsage,
  generateDreamVision,
  requestDreamVisionPrompt,
  type VisionStyle,
  type VisionUsage,
} from '../api/dream-vision';
import { fetchEntryPhotos, uploadEntryPhoto } from '../api/entry-photos';
import { useSession } from '../auth/SessionProvider';
import {
  IconArrowUpRight,
  IconCheck,
  IconClose,
  IconImage,
  IconLock,
  IconSpark,
} from '../components/Icons';

export interface DreamVisionFlowProps {
  open: boolean;
  onClose: () => void;
  /** Null while the entry is unsaved — the vision can still be made, but not attached. */
  entryId: string | null;
  dreamText: string;
  /** Fired after the image has been sealed and uploaded as an entry photo. */
  onAttached?: (photoId: string) => void;
}

type Step = 'approve' | 'generating' | 'result';

interface Version {
  generationId: string;
  signedUrl: string;
}

const REFINE_CHIPS = [
  'Darker',
  'More magical',
  'More realistic',
  'More surreal',
  'Softer lighting',
  'More cinematic',
  'Less scary',
  'Add more detail',
];

export function DreamVisionFlow({ open, onClose, entryId, dreamText, onAttached }: DreamVisionFlowProps) {
  if (!open) return null;
  // Remounted per opening so every piece of state — the prompt, the versions, a stale error — starts
  // clean, instead of each one needing its own reset effect.
  return (
    <DreamVisionDialog
      onClose={onClose}
      entryId={entryId}
      dreamText={dreamText}
      onAttached={onAttached}
    />
  );
}

function DreamVisionDialog({
  onClose,
  entryId,
  dreamText,
  onAttached,
}: Omit<DreamVisionFlowProps, 'open'>) {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [step, setStep] = useState<Step>('approve');
  const [usage, setUsage] = useState<VisionUsage | null>(null);
  const [prompt, setPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(true);
  const [removedCount, setRemovedCount] = useState(0);
  const pendingImage=useRef<{signature:string;id:string}|null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [freeRecreation, setFreeRecreation] = useState(true);
  const [refining, setRefining] = useState(false);

  const [instruction, setInstruction] = useState('');
  const [chips, setChips] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const refineRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const promptId = useId();
  const countId = useId();

  useDialogBehaviour(panelRef, onClose);

  // Both requests go out together: the balance decides whether the Generate button is even offered,
  // and the rewrite is the slow one, so serialising them would add the balance's latency to a step
  // the user is already waiting through.
  useEffect(() => {
    const controller = new AbortController();

    fetchVisionUsage(controller.signal)
      .then(setUsage)
      // A balance we could not read is not a reason to block the attempt — the Worker enforces the
      // quota regardless, and a wrong "you have none left" is worse than showing no figure.
      .catch(() => setUsage(null));

    requestDreamVisionPrompt(dreamText, entryId, controller.signal)
      .then((result) => {
        setPrompt(result.prompt);
        setRemovedCount(result.removedCount);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(dreamVisionErrorMessage(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setPromptLoading(false);
      });

    return () => controller.abort();
  }, [dreamText, entryId]);

  const selected = versions.find((v) => v.generationId === selectedId) ?? versions[versions.length - 1] ?? null;
  const words = countWords(prompt);
  const overCap = words > DREAM_VISION_PROMPT_MAX_WORDS;
  const balance = describeBalance(usage);
  const style = defaultVisionStyle(usage);

  const run = useCallback(
    async (outgoing: string, recreateOf: string | null) => {
      const returnTo: Step = recreateOf ? 'result' : 'approve';
      setError(null);
      setNote(null);
      setRefining(!!recreateOf);
      setStep('generating');

      const signature=JSON.stringify({outgoing,recreateOf,entryId});
      if(pendingImage.current?.signature!==signature) pendingImage.current={signature,id:crypto.randomUUID()};
      try {
        const result = await generateDreamVision({
          requestId:pendingImage.current.id,
          approvedPrompt: outgoing,
          entryId,
          recreateOf,
        });
        setVersions((current) => [...current, { generationId: result.generationId, signedUrl: result.signedUrl }]);
        setSelectedId(result.generationId);
        setFreeRecreation(result.freeRecreationAvailable);
        pendingImage.current=null;
        setInstruction('');
        setChips([]);
        setStep('result');
        // The balance moved; re-read it so a later recreation is offered honestly.
        fetchVisionUsage().then(setUsage).catch(() => undefined);
      } catch (err) {
        if(err instanceof Error && /could not create|could not save/i.test(err.message)) pendingImage.current=null;
        setError(dreamVisionErrorMessage(err));
        setStep(returnTo);
      }
    },
    [entryId],
  );

  async function onAttach() {
    if (!selected) return;

    if (!entryId) {
      setError('Save this dream first, then Imagine can attach the image to it. Your vision is already in your gallery.');
      return;
    }
    if (!dek) {
      setError('Unlock your vault to attach the image — entry photos are encrypted before they upload.');
      return;
    }

    setAttaching(true);
    setError(null);
    try {
      const blob = await fetchVisionImage(selected.signedUrl);
      // Appended after whatever is already on the entry, so attaching does not reorder the strip.
      const existing = await fetchEntryPhotos(dek, entryId).catch(() => []);
      const photo = await uploadEntryPhoto(dek, entryId, blob, existing.length);
      onAttached?.(photo.id);
      onClose();
    } catch (err) {
      setError(dreamVisionErrorMessage(err));
    } finally {
      setAttaching(false);
    }
  }

  async function onShare() {
    if (!selected) return;
    setNote(null);

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'My Dream Vision', url: selected.signedUrl });
        return;
      } catch {
        // Dismissing the share sheet is not a failure worth reporting.
        return;
      }
    }

    try {
      await navigator.clipboard.writeText(selected.signedUrl);
      setNote('Link copied. It stops working in about 15 minutes — save the image for anything longer.');
      return;
    } catch {
      // Fall through to a download, which is the only sharing left that outlives the link.
    }

    try {
      const blob = await fetchVisionImage(selected.signedUrl);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'dream-vision.png';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(dreamVisionErrorMessage(err));
    }
  }

  function onRecreate() {
    if (!selected) return;
    run(buildRefinedPrompt(prompt, chips, instruction), selected.generationId);
  }

  const showStage = step === 'generating';

  return createPortal(
    <>
      <div className="dv-scrim" onClick={showStage ? undefined : onClose} aria-hidden />
      <div className="dv-wrap" role="presentation">
        <div
          className="dv-panel"
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <header className="dv-head">
            <div className="dv-head-text">
              <h2 className="dv-title" id={titleId}>
                Imagine
              </h2>
              <p className="dv-sub">
                {step === 'result' ? 'Your dream, made visible.' : 'Your dream, as a picture — privately.'}
              </p>
            </div>
            <span className="dv-pill">
              <IconLock /> Private
            </span>
            <button className="dv-x" onClick={onClose} aria-label="Close Imagine">
              <IconClose />
            </button>
          </header>

          {step === 'approve' ? (
            <ApproveStep
              prompt={prompt}
              setPrompt={setPrompt}
              promptLoading={promptLoading}
              removedCount={removedCount}
              words={words}
              overCap={overCap}
              balance={balance}
              style={style}
              error={error}
              promptId={promptId}
              countId={countId}
              onGenerate={() => run(prompt, null)}
            />
          ) : null}

          {step === 'generating' ? <GeneratingStep refining={refining} /> : null}

          {step === 'result' && selected ? (
            <ResultStep
              versions={versions}
              selected={selected}
              onSelect={setSelectedId}
              freeRecreation={freeRecreation}
              chips={chips}
              setChips={setChips}
              instruction={instruction}
              setInstruction={setInstruction}
              refineRef={refineRef}
              onRecreate={onRecreate}
              onAttach={onAttach}
              onShare={onShare}
              attaching={attaching}
              canAttach={!!entryId}
              error={error}
              note={note}
            />
          ) : null}
        </div>
      </div>
    </>,
    document.body,
  );
}

/* ── Step one: approval ──────────────────────────────────────────────────────────────────────── */

interface Balance {
  label: string;
  detail: string | null;
  /** No generation is possible right now. */
  blocked: boolean;
  /** Blocked because the account is free and out of its introductory vision, not out of credits. */
  upgrade: boolean;
}

/**
 * What the balance line says, and whether Generate should exist at all.
 *
 * `blocked` defers to the gallery's `canGenerate` rather than re-deriving it, so the two screens can
 * never disagree about whether an account may generate; the branches below only choose the wording.
 */
function describeBalance(usage: VisionUsage | null): Balance {
  if (!usage) {
    return { label: 'Checking your balance…', detail: null, blocked: false, upgrade: false };
  }

  const blocked = !canGenerate(usage);

  if (usage.remainingDaily <= 0) {
    return {
      label: "Today's limit reached",
      detail: 'Five images in 24 hours is the ceiling. It lifts as the last day rolls past.',
      blocked,
      upgrade: false,
    };
  }

  if (usage.isPremium) {
    if (usage.remainingMonthly <= 0) {
      return {
        label: 'No credits left this month',
        detail: usage.resetsAt
          ? `Your image allowance returns on ${formatDate(usage.resetsAt)}.`
          : 'Your credits return at your renewal date.',
        blocked,
        upgrade: false,
      };
    }
    return {
      label: `${usage.remainingMonthly} ${usage.remainingMonthly === 1 ? 'credit' : 'credits'} left this month`,
      detail: usage.remainingDaily < usage.remainingMonthly ? `${usage.remainingDaily} more today.` : null,
      blocked,
      upgrade: false,
    };
  }

  if (usage.introAvailable) {
    return {
      label: 'Your one free vision',
      detail: 'It includes one recreation, in case the first is not quite right.',
      blocked,
      upgrade: false,
    };
  }

  return {
    label: 'Dream Vision is part of Premium',
    detail: "You've used your free vision. New Premium plans include up to 10 a month, within monthly fair use.",
    blocked,
    upgrade: true,
  };
}

function ApproveStep({
  prompt,
  setPrompt,
  promptLoading,
  removedCount,
  words,
  overCap,
  balance,
  style,
  error,
  promptId,
  countId,
  onGenerate,
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  promptLoading: boolean;
  removedCount: number;
  words: number;
  overCap: boolean;
  balance: Balance;
  style: VisionStyle | null;
  error: string | null;
  promptId: string;
  countId: string;
  onGenerate: () => void;
}) {
  const ready = !promptLoading && prompt.trim().length > 0 && !overCap && !balance.blocked;

  return (
    <div className="dv-body">
      {/* This paragraph is the feature, not a disclaimer around it, so it has to stay exactly true.
          It used to say the text below was sent "exactly as you leave it"; the Worker now appends a
          fixed style instruction, and the sentence says so — with the words themselves one tap
          away, because adding words to someone's prompt and describing them is not the same as
          showing them. */}
      <section className="dv-privacy" aria-label="How your dream is handled">
        <span className="dv-label">Two stages</span>
        <p>
          Oreia rewrites your dream into a de-identified description on its own servers — names and
          real places are taken out. What goes to the image provider is the text below, as you leave
          it, plus a fixed style instruction so every vision looks like Oreia. Your entry itself
          never goes.
        </p>
      </section>

      <StyleRow style={style} />

      <section className={`dv-balance${balance.blocked ? ' is-blocked' : ''}`} aria-label="Dream Vision balance">
        <span className="dv-label">Balance</span>
        <p className="dv-balance-label">{balance.label}</p>
        {balance.detail ? <p className="dv-balance-detail">{balance.detail}</p> : null}
      </section>

      <section className="dv-field">
        <div className="dv-field-head">
          <label className="dv-label" htmlFor={promptId}>
            The outgoing prompt
          </label>
          <span className={`dv-count${overCap ? ' is-over' : ''}`} id={countId} aria-live="polite">
            {words} / {DREAM_VISION_PROMPT_MAX_WORDS} words
          </span>
        </div>

        {promptLoading ? (
          <div className="dv-prompt-loading" role="status">
            <span className="ospin" aria-hidden />
            <span>Writing a description that gives nothing away…</span>
          </div>
        ) : (
          <textarea
            id={promptId}
            className="dv-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={7}
            aria-describedby={countId}
            placeholder="Describe what the image should show."
          />
        )}

        {removedCount > 0 ? (
          <p className="dv-removed">
            Oreia removed {removedCount} identifying {removedCount === 1 ? 'detail' : 'details'} from the
            description.
          </p>
        ) : null}

        {overCap ? (
          <p className="dv-warn" role="alert">
            Trim it to {DREAM_VISION_PROMPT_MAX_WORDS} words — anything past that is cut before it is sent.
          </p>
        ) : null}
      </section>

      {error ? (
        <p className="dv-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="dv-foot">
        {balance.upgrade ? (
          <Link className="dv-cta" to="/pricing">
            <IconSpark /> See Premium
          </Link>
        ) : balance.blocked ? (
          <p className="dv-blocked-note">{balance.detail ?? balance.label}</p>
        ) : (
          <button className="dv-cta" onClick={onGenerate} disabled={!ready}>
            <IconSpark /> Generate this vision
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The style the vision will be rendered in, and the exact words that buys.
 *
 * Rendered only when the server has told us what the style is. There is no bundled fallback on
 * purpose: a copy of the preamble kept in this file would eventually drift from the one the Worker
 * sends, and showing someone the wrong "exact words" is worse than showing none — the paragraph
 * above already says a style instruction is added, so silence here still leaves nothing hidden.
 *
 * A <details> rather than an always-open block: it is the prompt itself people came to check, and
 * seventy words of palette above the textarea would bury it.
 */
function StyleRow({ style }: { style: VisionStyle | null }) {
  if (!style) return null;

  return (
    <section className="dv-style" aria-label="Image style">
      <p className="dv-style-row">
        <span className="dv-label">Style</span>
        <span className="dv-style-dot" aria-hidden>
          ·
        </span>
        <span className="dv-style-name">{style.name}</span>
      </p>

      {style.preamble ? (
        <details className="dv-style-details">
          <summary>See the words this adds</summary>
          <p className="dv-style-preamble">{style.preamble}</p>
          <p className="dv-style-foot">Added after your text, and not counted against your 250 words.</p>
        </details>
      ) : null}
    </section>
  );
}

/* ── Step two: the wait ──────────────────────────────────────────────────────────────────────── */

function GeneratingStep({ refining }: { refining: boolean }) {
  return (
    <div className="dv-stage" role="status" aria-live="polite">
      <div className="dv-orb" aria-hidden>
        <span className="dv-orb-ring" />
        <span className="dv-orb-core" />
      </div>
      <h3 className="dv-stage-title">
        {refining ? 'Recreating your' : 'Creating your'} <em>Dream Vision…</em>
      </h3>
      <p className="dv-stage-note">
        This usually takes 15 to 30 seconds. You can close this — the vision will still be made, and
        it will be waiting in your gallery.
      </p>
    </div>
  );
}

/* ── Step three: the result, and refining it ─────────────────────────────────────────────────── */

function ResultStep({
  versions,
  selected,
  onSelect,
  freeRecreation,
  chips,
  setChips,
  instruction,
  setInstruction,
  refineRef,
  onRecreate,
  onAttach,
  onShare,
  attaching,
  canAttach,
  error,
  note,
}: {
  versions: Version[];
  selected: Version;
  onSelect: (id: string) => void;
  freeRecreation: boolean;
  chips: string[];
  setChips: (value: string[]) => void;
  instruction: string;
  setInstruction: (value: string) => void;
  refineRef: React.RefObject<HTMLInputElement | null>;
  onRecreate: () => void;
  onAttach: () => void;
  onShare: () => void;
  attaching: boolean;
  canAttach: boolean;
  error: string | null;
  note: string | null;
}) {
  const index = versions.findIndex((v) => v.generationId === selected.generationId);

  return (
    <div className="dv-body">
      <img className="dv-image" src={selected.signedUrl} alt="Your dream, generated as an image" />

      {versions.length > 1 ? (
        <div className="dv-versions" role="radiogroup" aria-label="Choose a version">
          {versions.map((version, i) => (
            <button
              key={version.generationId}
              className={`dv-version${version.generationId === selected.generationId ? ' is-on' : ''}`}
              role="radio"
              aria-checked={version.generationId === selected.generationId}
              onClick={() => onSelect(version.generationId)}
            >
              <img src={version.signedUrl} alt="" />
              <span>Version {i + 1}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="dv-actions">
        <button className="dv-action" onClick={onAttach} disabled={attaching}>
          <span className="dv-action-icon">{attaching ? <span className="ospin" aria-hidden /> : <IconCheck />}</span>
          <span>{versions.length > 1 ? `Use version ${index + 1}` : 'Use this vision'}</span>
        </button>
        <button className="dv-action" onClick={() => refineRef.current?.focus()}>
          <span className="dv-action-icon">
            <IconSpark />
          </span>
          <span>Recreate</span>
        </button>
        <button className="dv-action" onClick={onShare}>
          <span className="dv-action-icon">
            <IconArrowUpRight />
          </span>
          <span>Share</span>
        </button>
        <Link className="dv-action" to="/dreams">
          <span className="dv-action-icon">
            <IconImage />
          </span>
          <span>Gallery</span>
        </Link>
      </div>

      {!canAttach ? (
        <p className="dv-note">
          Save this dream and "Use this vision" will attach the image to it. Until then it lives in
          your gallery.
        </p>
      ) : null}

      {error ? (
        <p className="dv-error" role="alert">
          {error}
        </p>
      ) : null}
      {note ? <p className="dv-note">{note}</p> : null}

      <section className="dv-refine">
        <div className="dv-field-head">
          <span className="dv-label">Refine</span>
          <span className={`dv-cost${freeRecreation ? ' is-free' : ''}`}>
            {freeRecreation ? 'Next recreation is free' : 'Next recreation uses 1 credit'}
          </span>
        </div>

        <div className="dv-chips" role="group" aria-label="Quick changes">
          {REFINE_CHIPS.map((chip) => {
            const on = chips.includes(chip);
            return (
              <button
                key={chip}
                className={`dv-chip${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() => setChips(on ? chips.filter((c) => c !== chip) : [...chips, chip])}
              >
                {chip}
              </button>
            );
          })}
        </div>

        <div className="dv-refine-row">
          <input
            ref={refineRef}
            className="dv-refine-input"
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onRecreate();
            }}
            placeholder="Make it darker, with softer moonlight…"
            aria-label="What should change?"
          />
          <button className="dv-refine-go" onClick={onRecreate} aria-label="Recreate this vision">
            <IconSpark />
          </button>
        </div>
      </section>
    </div>
  );
}

/* ── Dialog plumbing ─────────────────────────────────────────────────────────────────────────── */

/**
 * Escape to close, focus moved in on open and returned on close, and Tab kept inside.
 *
 * Written here rather than reached for from SettingsKit because that dialog's trap is bound up with
 * its confirm/cancel pair; this one has to hold a textarea, a chip group and a version chooser.
 */
function useDialogBehaviour(panelRef: React.RefObject<HTMLDivElement | null>, onClose: () => void) {
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      previous?.focus?.();
    };
  }, [panelRef, onClose]);
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}
